import { FC, useEffect, useState } from "react";
import { Control, FieldValues, UseFormSetValue } from "react-hook-form";
import { useDispatch } from "react-redux";
import {
  FormController,
  MuiBox,
  MuiGrid,
  MuiSelect,
  MuiTextField,
  OptionsDataType,
} from "../../../../components";
import { CreateProgramTemplateProfileWrapper } from "./CreateProgramTemplateProfile.style";

import {
  useGetLineOfBusiness,
  useGetProgramClass,
  useGetProgramFrameWork,
  useGetSolutionCategory,
  useGetSolutionCategoryLob,
  useGetSolutionOffering,
} from "../../../../api/services/programTemplate/programTemplateApis";
import { MuiLink } from "../../../../components/link/MuiLink";
import {
  LANGUAGE_CODE,
  MAX_LENGTH_50,
  MAX_LENGTH_75,
  MDM_ACTIVITY_TYPE_KEY,
} from "../../../../helpers/constants";
import {
  isNotEmpty,
  setApiDataToDropDownOption,
} from "../../../../helpers/utils";
import { useGridContainer } from "../../../../hooks/useGridContainer";
import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import {
  toggleLoader,
  toggleToast,
} from "../../../../redux/slices/appState/appStateSlice";
import {
  NameMandatoryByLanguage,
  ProgramTemplateFormKey,
  ProgramTemplateProfileFormProps,
} from "../../modelsAndSchemas/Models";
import { createProgramSiteCoreKeys } from "../../siteCore/SiteCoreKeys";
import { commonSiteCoreKeys } from "../../../../helpers/constants/siteCoreKeys";

interface CreateProgramProfileProps extends FieldValues {
  control: Control<ProgramTemplateProfileFormProps>;
  pageId: string;
  setValue: UseFormSetValue<ProgramTemplateProfileFormProps>;
  handleProgramFrameworkChange: (id: string) => void;
  templateData?: ProgramTemplateProfileFormProps;
  setShowLocaleModal: () => void;
  nameMandatoryByLanguage: NameMandatoryByLanguage | undefined;
  handleProgramNameOnBlur: (value: string) => void;
}

export const CreateProgramTemplateProfile: FC<CreateProgramProfileProps> = ({
  control,
  pageId,
  setValue,
  handleProgramFrameworkChange,
  templateData,
  setShowLocaleModal,
  nameMandatoryByLanguage,
  handleProgramNameOnBlur,
}) => {
  const [solutionCategoryList, setSolutionCategoryList] = useState<
    OptionsDataType[]
  >([]);
  const [solutionCategoryLobList, setSolutionCategoryLobList] = useState<
    OptionsDataType[]
  >([]);
  const [programClassList, setProgramClassList] = useState<OptionsDataType[]>(
    [],
  );
  const {
    commonLabels,
    fetchLabel,
    fetchDropdownValues,
    fetchValidationMessage,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const dispatch = useDispatch();
  const { gridSizeMedium: gridSize, gridSizeMediumClass } = useGridContainer();

  const {
    data: lineOfBusinesses,
    isLoading: businessPending,
    isError: isLineOfBusinessError,
  } = useGetLineOfBusiness(LANGUAGE_CODE.EN_US);
  const {
    data: programFrameWork,
    isLoading: frameWorkPending,
    isError: isProgramFrameWorkError,
  } = useGetProgramFrameWork(
    LANGUAGE_CODE.EN_US,
    MDM_ACTIVITY_TYPE_KEY.program,
  );
  const {
    data: solutionCategories,
    isLoading: categoryPending,
    isError: isSolutionCategoryError,
    refetchWithParams: refetchSolutionCategory,
  } = useGetSolutionCategory();
  const {
    data: solutionOfferings,
    isLoading: offeringPending,
    isError: isSolutionOfferingError,
  } = useGetSolutionOffering(LANGUAGE_CODE.EN_US);
  const {
    data: solutionCategoriesLob,
    isLoading: businessLobPending,
    refetchWithParams: refetchSolutionCategoryLob,
    isError: isSolutionCategoryLobError,
  } = useGetSolutionCategoryLob();
  const {
    data: programClasses,
    isLoading: classPending,
    refetchWithParams: refetchProgramClass,
    isError: isProgramClassError,
  } = useGetProgramClass();

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible:
          businessPending ||
          frameWorkPending ||
          categoryPending ||
          offeringPending ||
          businessLobPending ||
          classPending,
        message: commonLabels.loadingPleaseWait,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [
    businessPending,
    frameWorkPending,
    categoryPending,
    offeringPending,
    businessLobPending,
    classPending,
  ]);

  useEffect(() => {
    if (
      isLineOfBusinessError ||
      isProgramFrameWorkError ||
      isSolutionCategoryError ||
      isSolutionCategoryLobError ||
      isSolutionOfferingError ||
      isProgramClassError
    ) {
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
          type: "error",
        }),
      );
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [
    isLineOfBusinessError,
    isProgramFrameWorkError,
    isSolutionCategoryError,
    isSolutionCategoryLobError,
    isSolutionOfferingError,
    isProgramClassError,
  ]);

  useEffect(() => {
    if (isNotEmpty(templateData)) {
      handleSolutionCategoryChange(templateData?.solutionCategory ?? "");
      handleSolutionOfferingChange(templateData?.solutionOffering ?? "");
      handleProductLineChange(templateData?.lineOfBusiness ?? "");
    }
  }, [templateData]);

  const handleProductLineChange = async (id: string) => {
    if (isNotEmpty(id)) {
      refetchSolutionCategory({
        languageCode: LANGUAGE_CODE.EN_US,
        lineOfBusinessId: id,
      });
    }
    setSolutionCategoryList([]);
    setSolutionCategoryLobList([]);
    setValue(ProgramTemplateFormKey.solutionCategory, "");
    setValue(ProgramTemplateFormKey.solutionCategoryLob, "");
  };

  const handleSolutionCategoryChange = async (id: string) => {
    if (isNotEmpty(id)) {
      refetchSolutionCategoryLob({
        languageCode: LANGUAGE_CODE.EN_US,
        solutionCategoryId: id,
      });
    }
    setSolutionCategoryLobList([]);
    setValue(ProgramTemplateFormKey.solutionCategoryLob, "");
  };

  const handleSolutionOfferingChange = async (id: string) => {
    if (isNotEmpty(id)) {
      refetchProgramClass({
        languageCode: LANGUAGE_CODE.EN_US,
        solutionOfferingId: id,
      });
    }
    setProgramClassList([]);
    setValue(ProgramTemplateFormKey.programClass, "");
  };

  useEffect(() => {
    if (isNotEmpty(solutionCategories)) {
      setSolutionCategoryList(setApiDataToDropDownOption(solutionCategories));
    } else {
      setSolutionCategoryList([]);
    }
  }, [solutionCategories]);

  useEffect(() => {
    if (isNotEmpty(solutionCategoriesLob)) {
      setSolutionCategoryLobList(
        setApiDataToDropDownOption(solutionCategoriesLob),
      );
    } else {
      setSolutionCategoryLobList([]);
    }
  }, [solutionCategoriesLob]);

  useEffect(() => {
    if (isNotEmpty(programClasses)) {
      setProgramClassList(setApiDataToDropDownOption(programClasses));
    } else {
      setProgramClassList([]);
    }
  }, [programClasses]);

  const fieldSpecialCharactersTooltipMessage = fetchValidationMessage(
    commonSiteCoreKeys.fieldSpecialCharactersTooltipMessage,
  );
  const tooltipheader = fetchValidationMessage(
    commonSiteCoreKeys.allowedSpecialCharacters,
  );

  return (
    <CreateProgramTemplateProfileWrapper>
      <form data-testid="create-template-profile">
        <MuiGrid container className="form-container">
          {nameMandatoryByLanguage && (
            <MuiGrid
              size={gridSize}
              className={`${gridSizeMediumClass} program-name`}
            >
              <FormController
                control={control}
                name={ProgramTemplateFormKey.programNameByLanguage}
              >
                <MuiTextField
                  label={fetchLabel(
                    createProgramSiteCoreKeys.profileSection,
                    createProgramSiteCoreKeys.nameLabel,
                  )}
                  placeholder={nameMandatoryByLanguage.placeholderKey}
                  id={`${pageId}-${ProgramTemplateFormKey.programName}`}
                  isMandatory
                  maxLength={MAX_LENGTH_75}
                  onBlur={(e) => handleProgramNameOnBlur(e.target.value)}
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>
          )}
          <MuiGrid
            size={gridSize}
            className={`${gridSizeMediumClass} program-name ${
              nameMandatoryByLanguage?.key === LANGUAGE_CODE.FR_FR
                ? "non-mandatory-language"
                : ""
            }`}
          >
            <MuiBox className="program-name">
              <FormController
                control={control}
                name={ProgramTemplateFormKey.programName}
              >
                <MuiTextField
                  label={fetchLabel(
                    createProgramSiteCoreKeys.profileSection,
                    nameMandatoryByLanguage
                      ? createProgramSiteCoreKeys.nameWithUs
                      : createProgramSiteCoreKeys.nameLabel,
                  )}
                  placeholder={commonLabels.inputPlaceHolder}
                  id={`${pageId}-${ProgramTemplateFormKey.programName}`}
                  isMandatory
                  maxLength={MAX_LENGTH_50}
                  onBlur={
                    !nameMandatoryByLanguage
                      ? (e) => handleProgramNameOnBlur(e.target.value)
                      : undefined
                  }
                  requiredARIALabel={commonARIALabel.required}
                  tooltipMessage={fieldSpecialCharactersTooltipMessage}
                  tooltipHeaderTitle={tooltipheader}
                />
              </FormController>
              <MuiLink
                className="manage-locale-link"
                onClick={setShowLocaleModal}
                id={`${pageId}-locale`}
                data-testid={`${pageId}-locale`}
              >
                {commonLabels.manageLocale}
              </MuiLink>
            </MuiBox>
          </MuiGrid>
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={ProgramTemplateFormKey.programFrameWork}
            >
              <MuiSelect
                label={fetchLabel(
                  createProgramSiteCoreKeys.profileSection,
                  createProgramSiteCoreKeys.programFrameworkLabel,
                )}
                options={setApiDataToDropDownOption(programFrameWork)}
                visibleDefaultSelectOption
                value=""
                id={`${pageId}-${ProgramTemplateFormKey.programFrameWork}`}
                isMandatory
                onChange={(e) =>
                  handleProgramFrameworkChange(e.target.value as string)
                }
                placeholder={commonLabels.selectPlaceHolder}
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
          </MuiGrid>
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={ProgramTemplateFormKey.solutionOffering}
            >
              <MuiSelect
                label={fetchLabel(
                  createProgramSiteCoreKeys.profileSection,
                  createProgramSiteCoreKeys.nextViewLabel,
                )}
                options={setApiDataToDropDownOption(solutionOfferings)}
                visibleDefaultSelectOption
                value=""
                id={`${pageId}-${ProgramTemplateFormKey.solutionOffering}`}
                isMandatory
                onChange={(e) =>
                  handleSolutionOfferingChange(e.target.value as string)
                }
                placeholder={commonLabels.selectPlaceHolder}
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
          </MuiGrid>
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={ProgramTemplateFormKey.lineOfBusiness}
            >
              <MuiSelect
                label={fetchLabel(
                  createProgramSiteCoreKeys.profileSection,
                  createProgramSiteCoreKeys.productLine,
                )}
                options={setApiDataToDropDownOption(lineOfBusinesses)}
                visibleDefaultSelectOption
                value=""
                id={`${pageId}-${ProgramTemplateFormKey.lineOfBusiness}`}
                isMandatory
                placeholder={commonLabels.selectPlaceHolder}
                requiredARIALabel={commonARIALabel.required}
                onChange={(e) =>
                  handleProductLineChange(e.target.value as string)
                }
              />
            </FormController>
          </MuiGrid>
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={ProgramTemplateFormKey.solutionCategory}
            >
              <MuiSelect
                label={fetchLabel(
                  createProgramSiteCoreKeys.profileSection,
                  createProgramSiteCoreKeys.solutionCategoryLabel,
                )}
                options={solutionCategoryList}
                visibleDefaultSelectOption
                value=""
                id={`${pageId}-${ProgramTemplateFormKey.solutionCategory}`}
                isMandatory
                onChange={(e) =>
                  handleSolutionCategoryChange(e.target.value as string)
                }
                placeholder={commonLabels.selectPlaceHolder}
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
          </MuiGrid>
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={ProgramTemplateFormKey.solutionCategoryLob}
            >
              <MuiSelect
                label={fetchLabel(
                  createProgramSiteCoreKeys.profileSection,
                  createProgramSiteCoreKeys.solutionSubCategory,
                )}
                options={solutionCategoryLobList}
                visibleDefaultSelectOption
                value=""
                id={`${pageId}-${ProgramTemplateFormKey.solutionCategoryLob}`}
                isMandatory
                placeholder={commonLabels.selectPlaceHolder}
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
          </MuiGrid>
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={ProgramTemplateFormKey.programClass}
            >
              <MuiSelect
                label={fetchLabel(
                  createProgramSiteCoreKeys.profileSection,
                  createProgramSiteCoreKeys.nextProgramClassLabel,
                )}
                options={programClassList}
                visibleDefaultSelectOption
                value=""
                id={`${pageId}-${ProgramTemplateFormKey.programClass}`}
                isMandatory
                placeholder={commonLabels.selectPlaceHolder}
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
          </MuiGrid>
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={ProgramTemplateFormKey.duration}
            >
              <MuiTextField
                label={fetchLabel(
                  createProgramSiteCoreKeys.profileSection,
                  createProgramSiteCoreKeys.durationMonthsLabel,
                )}
                placeholder={commonLabels.inputPlaceHolder}
                type="number"
                id={`${pageId}-${ProgramTemplateFormKey.duration}`}
                isMandatory
                requiredARIALabel={commonARIALabel.required}
                inputNumberWithoutDecimal
              />
            </FormController>
          </MuiGrid>
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={ProgramTemplateFormKey.closingReasonType}
            >
              <MuiSelect
                label={fetchLabel(
                  createProgramSiteCoreKeys.profileSection,
                  createProgramSiteCoreKeys.closingReasonType,
                )}
                options={fetchDropdownValues(
                  createProgramSiteCoreKeys.profileSection,
                  createProgramSiteCoreKeys.closingReasonTypeList,
                )}
                visibleDefaultSelectOption
                value=""
                id={`${pageId}-${ProgramTemplateFormKey.closingReasonType}`}
                isMandatory
                placeholder={commonLabels.selectPlaceHolder}
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
          </MuiGrid>
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={ProgramTemplateFormKey.status}
            >
              <MuiSelect
                label={fetchLabel(
                  createProgramSiteCoreKeys.profileSection,
                  createProgramSiteCoreKeys.statusLabel,
                )}
                options={fetchDropdownValues(
                  createProgramSiteCoreKeys.profileSection,
                  createProgramSiteCoreKeys.statusList,
                ).filter(
                  (data) => data.value !== createProgramSiteCoreKeys.statusAll,
                )}
                value=""
                id={`${pageId}-${ProgramTemplateFormKey.status}`}
                visibleDefaultSelectOption
                placeholder={commonLabels.selectPlaceHolder}
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
          </MuiGrid>
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={ProgramTemplateFormKey.nextAccess}
            >
              <MuiSelect
                label={fetchLabel(
                  createProgramSiteCoreKeys.profileSection,
                  createProgramSiteCoreKeys.nextAccessLabel,
                )}
                options={fetchDropdownValues(
                  createProgramSiteCoreKeys.profileSection,
                  createProgramSiteCoreKeys.nextAccessList,
                )}
                visibleDefaultSelectOption
                value=""
                id={`${pageId}-${ProgramTemplateFormKey.nextAccess}`}
                isMandatory
                placeholder={commonLabels.selectPlaceHolder}
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
          </MuiGrid>
        </MuiGrid>
      </form>
    </CreateProgramTemplateProfileWrapper>
  );
};
